import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AppHeader } from "@/components/layout/AppHeader";
import { Footer } from "@/components/layout/Footer";
import { useToolsData, useCategoriesData } from "@/hooks/useToolsData";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { FeaturedFreeResources } from "@/components/homepage/FeaturedFreeResources";
import { StatsSection } from "@/components/homepage/StatsSection";
import { FeaturedToolsSection } from "@/components/homepage/FeaturedToolsSection";
import { SearchAndFilter } from "@/components/homepage/SearchAndFilter";
import { ToolsGrid } from "@/components/homepage/ToolsGrid";
import { PaginationControls } from "@/components/homepage/PaginationControls";
import { PaginationInfo } from "@/components/homepage/PaginationInfo";
import { JigsoPromotion } from "@/components/homepage/JigsoPromotion";

const TOOLS_PER_PAGE = 9;
export default function Index() {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: tools = [], isLoading: toolsLoading } = useToolsData();
  const { data: categories = [], isLoading: categoriesLoading } = useCategoriesData();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [generationsThisMonth, setGenerationsThisMonth] = useState<number | null>(null);
  const [questionsCount, setQuestionsCount] = useState<number | null>(null);
  type Trend = { value: number; direction: 'up' | 'down' };
  const [generationsTrend, setGenerationsTrend] = useState<Trend | undefined>(undefined);
  const [questionsTrend, setQuestionsTrend] = useState<Trend | undefined>(undefined);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        let { count: gensCount } = await supabase
          .from('generations')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', firstOfMonth.toISOString());

        let { count: gensPrevCount } = await supabase
          .from('generations')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', firstOfPrevMonth.toISOString())
          .lt('created_at', firstOfMonth.toISOString());

        let { count: qCount } = await supabase
          .from('questions')
          .select('*', { count: 'exact', head: true });

        let { count: qPrevTotal } = await supabase
          .from('questions')
          .select('*', { count: 'exact', head: true })
          .lt('created_at', firstOfMonth.toISOString());

        const offsetGenerations = 45;
        gensCount = (gensCount ?? 0) + offsetGenerations;
        gensPrevCount = (gensPrevCount ?? 0) + offsetGenerations;
      

        setGenerationsThisMonth(gensCount ?? 0);
        setQuestionsCount(qCount ?? 0);

        // Compute generation trend (current month vs previous month)
        const currentGens = gensCount ?? 0;
        const prevGens = gensPrevCount ?? 0;
        if (currentGens === prevGens) {
          setGenerationsTrend({ value: 0, direction: 'up' });
        } else if (prevGens === 0) {
          setGenerationsTrend({ value: currentGens > 0 ? 100 : 0, direction: currentGens > 0 ? 'up' : 'down' });
        } else {
          const diff = currentGens - prevGens;
          const pct = Math.round(Math.abs((diff / prevGens) * 100));
          setGenerationsTrend({ value: pct, direction: diff >= 0 ? 'up' : 'down' });
        }

        // Compute questions trend (total now vs total end of last month)
        const currentQTotal = qCount ?? 0;
        const prevQTotal = qPrevTotal ?? 0;
        if (currentQTotal === prevQTotal) {
          setQuestionsTrend({ value: 0, direction: 'up' });
        } else if (prevQTotal === 0) {
          setQuestionsTrend({ value: currentQTotal > 0 ? 100 : 0, direction: currentQTotal > 0 ? 'up' : 'down' });
        } else {
          const qDiff = currentQTotal - prevQTotal;
          const qPct = Math.round(Math.abs((qDiff / prevQTotal) * 100));
          setQuestionsTrend({ value: qPct, direction: qDiff >= 0 ? 'up' : 'down' });
        }
      } catch (e) {
        setGenerationsThisMonth(228);
        setQuestionsCount(144);
        setGenerationsTrend(undefined);
        setQuestionsTrend(undefined);
      }
    };

    fetchStats();
  }, []);

  const featuredTools = useMemo(() => {
    return tools.filter((tool) => tool.is_featured && tool.is_active && !tool.is_coming_soon);
  }, [tools]);

  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      const matchesSearch =
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.tags.some((tag) => tag.name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = selectedCategory === "All" || tool.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [tools, searchTerm, selectedCategory]);

  const totalPages = Math.ceil(filteredTools.length / TOOLS_PER_PAGE);

  const paginatedTools = useMemo(() => {
    const startIndex = (currentPage - 1) * TOOLS_PER_PAGE;
    return filteredTools.slice(startIndex, startIndex + TOOLS_PER_PAGE);
  }, [filteredTools, currentPage]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleToolClick = (tool: any) => {
    if (tool.is_active && !tool.is_coming_soon) {
      navigate(`${prefix}/tools/${tool.slug || tool.id}`);
    }
  };

  const isLoadingAny = toolsLoading || categoriesLoading;

  return (
    <AppLayout>
      <AppHeader
        title={t('homepage.header.title')}
        subtitle={t('homepage.header.subtitle')}
        size="large"
        showChips
        showLogo
      />

      <StatsSection
        toolsCount={tools.length}
        generationsThisMonth={generationsThisMonth ?? 0}
        questionsCount={questionsCount ?? 0}
        user={user}
        prefix={prefix}
        loading={isLoadingAny}
        generationsTrend={generationsTrend}
        questionsTrend={questionsTrend}
      />

      <div className="max-w-6xl mx-auto px-4">
        <FeaturedToolsSection
          featuredTools={featuredTools}
          loading={isLoadingAny}
          onToolClick={handleToolClick}
        />

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">{t('homepage.toolsGrid.allTools')}</h2>
        </div>

        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
        />

        {isLoadingAny && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('homepage.toolsGrid.loadingTools')}</p>
          </div>
        )}

        {!isLoadingAny && (
          <ToolsGrid tools={paginatedTools} onToolClick={handleToolClick} />
        )}

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />

        <PaginationInfo
          currentPage={currentPage}
          pageSize={TOOLS_PER_PAGE}
          totalItems={filteredTools.length}
        />

        <FeaturedFreeResources />

        <JigsoPromotion />
      </div>
      <Footer />
    </AppLayout>
  );
}